<script lang="ts">
  import EggMark from '../lib/design/EggMark.svelte';
  import { link, href } from '../lib/router.svelte';
  import { session, ApiError } from '../lib/session.svelte';

  // Invite-only signup: `/signup?invite=<token>`. The token resolves to the
  // invited email + plan; the email is locked and only a password is set.
  let inviteToken = $state('');
  let email = $state('');
  let plan = $state('');
  let password = $state('');
  let busy = $state(false);
  let loading = $state(true);
  let error = $state('');

  $effect(() => {
    const p = new URLSearchParams(location.search);
    const t = p.get('invite') ?? '';
    if (t) inviteToken = t;
    void resolve(t);
  });

  async function resolve(token: string) {
    if (!token) {
      loading = false;
      error = 'This invite link is missing a code. Ask for a fresh invite.';
      return;
    }
    try {
      const inv = await session.invite(token);
      if (!inv) {
        loading = false;
        error = 'This invite is invalid or expired. Ask for a fresh invite.';
        return;
      }
      email = inv.email;
      plan = inv.plan;
      loading = false;
    } catch {
      loading = false;
      error = "Couldn't check this invite. Try again in a moment.";
    }
  }

  async function submit(e: SubmitEvent) {
    e.preventDefault();
    if (busy || loading || !password || !inviteToken) return;
    busy = true;
    error = '';
    try {
      await session.signup(email, password, inviteToken);
      // App unmounts this screen once session.ready flips.
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Something went wrong. Try again.';
    } finally {
      busy = false;
    }
  }
</script>

<div class="connect">
  <div class="brand">
    <EggMark size={44} />
    <h1>Kip</h1>
    <p class="sub">Create your account.</p>
  </div>

  {#if loading}
    <p class="hint">Checking your invite…</p>
  {:else if !inviteToken || error === 'This invite is invalid or expired. Ask for a fresh invite.' || error === 'This invite link is missing a code. Ask for a fresh invite.'}
    <p class="error" role="alert">{error}</p>
    <p class="foot">Kip is invite-only. <a class="link" href={href('/')} use:link>Sign in</a></p>
  {:else}
    {#if error}<p class="error" role="alert">{error}</p>{/if}
    <form onsubmit={submit}>
      <label>
        <span class="eyebrow">Email</span>
        <input type="email" value={email} disabled />
      </label>
      {#if plan}<p class="hint">Invited to the <strong>{plan}</strong> plan.</p>{/if}
      <label>
        <span class="eyebrow">Password</span>
        <input type="password" autocomplete="new-password" bind:value={password} required minlength="8" />
        <span class="hint">At least 8 characters.</span>
      </label>
      <button class="go" type="submit" disabled={busy || !password || password.length < 8}>
        {busy ? 'Creating…' : 'Create account'}
      </button>
    </form>
  {/if}

  <p class="foot">
    Already have an account?
    <a class="link" href={href('/')} use:link>Sign in</a>
  </p>
</div>

<style>
  .connect {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 100dvh;
    max-width: 360px;
    margin: 0 auto;
    padding: 32px 24px calc(32px + env(safe-area-inset-bottom));
    gap: 28px;
  }
  .brand {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 8px;
  }
  h1 {
    font-family: var(--serif);
    font-size: 34px;
    letter-spacing: -0.02em;
  }
  .sub {
    color: var(--ink-soft);
    font-size: 14px;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  input {
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 12px 14px;
    background: var(--cream-2);
    font-size: 16px;
  }
  input:focus-visible {
    outline: 2px solid var(--rooster);
    outline-offset: 1px;
  }
  .hint {
    font-size: 11px;
    color: var(--ink-faint);
  }
  .go {
    margin-top: 4px;
    border-radius: 12px;
    padding: 13px 20px;
    background: var(--rooster);
    color: var(--cream);
    font-family: var(--mono);
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.03em;
  }
  .go:disabled {
    opacity: 0.4;
  }
  .error {
    color: var(--rooster-deep);
    font-size: 13px;
  }
  .foot {
    text-align: center;
    color: var(--ink-faint);
    font-size: 12px;
  }
  .link {
    color: var(--rooster);
    text-decoration: underline;
  }
</style>
